import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { isIosDevice, isStandaloneDisplay } from "../pwa/pwaInstallVisibility";
import { createNotificationServiceClient, describeTestNotificationResult } from "./client";

export type NotificationPermissionState = "checking" | "unsupported" | "config-unavailable" | "blocked" | "not-enabled" | "enabled";
// "empty" is distinct from "success": the request itself succeeded, but there was nothing to
// deliver to (no registered devices), so it should not read as a confirmed delivery.
export type TestSendStatus = "idle" | "sending" | "success" | "empty" | "error";

// The failure stages that can occur *after* the user has been asked for permission. Genuine
// denial is deliberately NOT in here -- it is not an error, it moves `state` to "blocked". Every
// kind below leaves `state` at "not-enabled" so the user can retry, and maps to fixed,
// privacy-safe copy: a raw DOMException / WebKit / push-provider / backend message is never shown.
export type EnableFailureKind =
  | "subscribe-failed"
  | "push-unsupported"
  | "vapid-config"
  | "registration-failed"
  | "unknown";

const ENABLE_FAILURE_COPY: Record<EnableFailureKind, string> = {
  // WebKit rejects pushManager.subscribe() with NotAllowedError ("User denied push permission.")
  // for non-denial conditions too -- permission is already "granted" when this runs, so this must
  // never read as a denial.
  "subscribe-failed": "Notifications couldn't be enabled on this device. You can continue using MedoPilot normally.",
  "push-unsupported": "This device can't receive MedoPilot notifications. You can continue using MedoPilot normally.",
  "vapid-config": "Notifications aren't available in this MedoPilot environment yet. You can continue using MedoPilot normally.",
  "registration-failed": "We couldn't finish setting up notifications on this device. You can try again.",
  unknown: "Notifications couldn't be enabled on this device. You can continue using MedoPilot normally.",
};

// The last classified enable() failure, shared at module scope so the two independent consumers
// of this hook stay consistent: the automatic NotificationEnablePrompt (which hides itself the
// moment a failure occurs -- it must never sit over Home showing an error) and the permanent
// Settings > Notifications surface (which keeps showing the classified failure and offers retry).
// In-memory only: a fresh page load starts clean.
let sharedFailureKind: EnableFailureKind | null = null;
const failureKindListeners = new Set<() => void>();

function setSharedFailureKind(kind: EnableFailureKind | null) {
  if (sharedFailureKind === kind) {
    return;
  }
  sharedFailureKind = kind;
  for (const listener of failureKindListeners) {
    listener();
  }
}

function subscribeFailureKind(listener: () => void) {
  failureKindListeners.add(listener);
  return () => {
    failureKindListeners.delete(listener);
  };
}

function getSharedFailureKind() {
  return sharedFailureKind;
}

export function __resetNotificationPermissionForTests() {
  sharedFailureKind = null;
  failureKindListeners.clear();
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }
  return outputArray;
}

export function useNotificationPermission() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.notificationService?.apiBaseUrl;
  const vapidPublicKey = config?.notificationService?.vapidPublicKey ?? null;

  const [state, setState] = useState<NotificationPermissionState>("checking");
  const [enabling, setEnabling] = useState(false);
  const failureKind = useSyncExternalStore(subscribeFailureKind, getSharedFailureKind);
  // In-memory, app/session-lifecycle auto-dismissal for the automatic NotificationEnablePrompt.
  // Set ONLY by failEnable() -- the moment an enable() attempt fails after permission was granted,
  // so a failed setup never leaves the large invitation covering Home. There is deliberately no
  // user-triggered "Not now": MedoPilot requires notifications for core features, so the banner
  // has a single action and no voluntary dismiss path. NotificationEnablePrompt is mounted once at
  // the app root (not route-scoped), so this survives navigation for the life of the loaded app.
  // Deliberately not persisted: a fresh load is eligible to invite again.
  const [enablePromptDismissed, setEnablePromptDismissed] = useState(false);
  const [testStatus, setTestStatus] = useState<TestSendStatus>("idle");
  const [testMessage, setTestMessage] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }
    return createNotificationServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  // Genuine browser/device capability -- independent of whether this MedoPilot environment has
  // notifications configured yet. Keeping these separate lets "unsupported" mean only "this
  // browser/device cannot do this" and never "MedoPilot hasn't set this up here."
  const hasBrowserSupport = Boolean(
    typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window,
  );
  // A VAPID public key that has not been provisioned for this environment yet.
  const isConfigured = Boolean(apiBaseUrl && vapidPublicKey);
  const isSupported = hasBrowserSupport && isConfigured;

  const detectState = useCallback(async () => {
    if (!hasBrowserSupport) {
      setState("unsupported");
      return;
    }

    if (!isConfigured) {
      setState("config-unavailable");
      return;
    }

    if (Notification.permission === "denied") {
      setState("blocked");
      return;
    }

    if (Notification.permission !== "granted") {
      setState("not-enabled");
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setState(subscription ? "enabled" : "not-enabled");
    } catch {
      setState("not-enabled");
    }
  }, [hasBrowserSupport, isConfigured]);

  useEffect(() => {
    void detectState();
  }, [detectState]);

  const enable = useCallback(async () => {
    if (!isSupported || !client || !vapidPublicKey) {
      return;
    }

    setEnabling(true);
    setSharedFailureKind(null);

    // A classified failure. The copy is fixed and privacy-safe (never a raw browser/provider/
    // backend message); `state` stays "not-enabled" so retry is always possible; and the large
    // automatic invitation is dismissed so it never sits over Home after a failure. Retry and the
    // classified status then live in Settings > Notifications, the permanent management surface.
    const failEnable = (kind: EnableFailureKind) => {
      setSharedFailureKind(kind);
      setEnablePromptDismissed(true);
    };

    try {
      let permission: NotificationPermission;
      try {
        // Only ever called from this explicit, user-initiated action -- never on mount/page load.
        permission = await Notification.requestPermission();
      } catch {
        failEnable("unknown");
        return;
      }

      // Genuine denial is the ONLY outcome that moves state to "blocked" -- and it also removes the
      // automatic invitation (state leaves "not-enabled"). Every failure past this point leaves
      // state at "not-enabled" and is shown as fixed, classified, privacy-safe copy -- never a raw
      // DOMException / WebKit / provider / backend string, and never as "denied".
      if (permission === "denied") {
        setState("blocked");
        return;
      }
      if (permission !== "granted") {
        setState("not-enabled");
        return;
      }

      let applicationServerKey: Uint8Array<ArrayBuffer>;
      try {
        applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      } catch {
        failEnable("vapid-config");
        return;
      }

      if (!("PushManager" in window) || !("serviceWorker" in navigator)) {
        failEnable("push-unsupported");
        return;
      }

      let subscription: PushSubscription | null;
      try {
        const registration = await navigator.serviceWorker.ready;
        // Reuse an already-existing subscription for this device instead of creating a duplicate.
        subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          });
        }
      } catch {
        // Permission is already "granted" here; a rejection is a push-setup failure, not a
        // denial -- including WebKit's misleading NotAllowedError "User denied push permission."
        failEnable("subscribe-failed");
        return;
      }

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        failEnable("subscribe-failed");
        return;
      }

      try {
        await client.registerSubscription({
          endpoint: json.endpoint,
          keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
          ...(typeof json.expirationTime === "number" ? { expirationTime: json.expirationTime } : {}),
        });
      } catch {
        failEnable("registration-failed");
        return;
      }

      setSharedFailureKind(null);
      setState("enabled");
    } finally {
      setEnabling(false);
    }
  }, [isSupported, client, vapidPublicKey]);

  useEffect(() => {
    if (testStatus === "idle" || testStatus === "sending") {
      return undefined;
    }
    const timeout = window.setTimeout(() => setTestStatus("idle"), 4000);
    return () => window.clearTimeout(timeout);
  }, [testStatus]);

  const sendTest = useCallback(async () => {
    if (!client) {
      return;
    }

    setTestStatus("sending");
    setTestError(null);
    setTestMessage(null);

    try {
      const result = await client.sendTestNotification();
      setTestMessage(describeTestNotificationResult(result));
      setTestStatus(result.requested > 0 ? "success" : "empty");
    } catch (nextError) {
      setTestError(nextError instanceof Error ? nextError.message : "We could not send a test notification.");
      setTestStatus("error");
    }
  }, [client]);

  // Fixed, classified, privacy-safe copy for the last enable() failure -- or null. Consumers must
  // render this and never a raw exception message.
  const error = failureKind ? ENABLE_FAILURE_COPY[failureKind] : null;

  // The automatic invitation is for *installed standalone* use only: browser use stays
  // first-class and is never nagged (Settings remains the management surface for it). Shown only
  // while: signed in; running standalone; permission has genuinely never been asked and this
  // device isn't already subscribed (`state === "not-enabled"` also excludes "enabled",
  // "blocked", "unsupported", "config-unavailable"); and `failEnable()` hasn't auto-dismissed it
  // because an enable() attempt failed after permission was granted (retry then lives in Settings,
  // so the invitation never sits over Home showing an error). There is no user-triggered dismiss.
  const showEnablePrompt =
    auth.isAuthenticated && isStandaloneDisplay() && state === "not-enabled" && !enablePromptDismissed;

  // Settings-only hint: an iOS/iPadOS browser tab cannot do Web Push at all (no PushManager),
  // which surfaces as `state === "unsupported"`. Home Screen installation is the prerequisite for
  // the supported experience -- Settings should say so rather than dead-end.
  const iosNeedsInstall = isIosDevice() && !isStandaloneDisplay();

  return {
    state,
    enabling,
    error,
    failureKind,
    enable,
    iosNeedsInstall,
    testStatus,
    testMessage,
    testError,
    sendTest,
    showEnablePrompt,
  };
}
